import { useContext, useRef, useState, useEffect, FunctionComponent } from 'react';
import * as React from 'react';
import { graphql, createRefetchContainer } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';

import classnames from 'classnames';
import * as tracking from 'dibs-tracking';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import ArrowRight from 'dibs-icons/exports/ArrowRight';
import { ProductDetails } from './ProductDetails';
import { ProductSwiper } from './ProductSwiper';

import get from 'lodash.get';
import { TileContext } from '../../helpers/TileContext';
import { ImageSizeType } from '../../types/StyleSizes';

type TileContextProps = {
    children: React.ReactNode;
    imageSize: ImageSizeType;
};

import { ProductCarousel_item as ProductItemProp } from './__generated__/ProductCarousel_item.graphql';

import styles from '../styles.scss';

export type ProductCarouselProps = {
    disableLinks?: boolean;
    pdpUrl: string;
    index: number;
    item: ProductItemProp;
    itemsPerRow: number;
    isHovering: boolean;
    isQuickViewActive: boolean;
    isMobile: boolean;
    title: string;
    onClick?: () => void;
    onContentLoaded?: () => void;
    showQuickView?: boolean;
    openQuickView?: () => void;
    showMoreOptionsBadge: boolean;
    showLightBox?: boolean;
    dimensions?: string;
    showQuickViewIcon?: boolean;
    useLoFiLazyLoader: boolean;
    imageLoadVerticalOffset?: number;
    srcSetSizes?: string;
    srcSetWidths?: Array<number>;
    srcSetQuality?: number;
    hideDivider?: boolean;
    hideProductDetails?: boolean;
    hideImagePadding?: boolean;
    showSwipeIndicator?: boolean;
    // issues with ProductCarousel (relay = createRefetchContainer)
    // and Product (relay = createFragmentContainer) components
    relay: $TSFixMe;
};

export const ProductCarouselComponent: FunctionComponent<ProductCarouselProps> = props => {
    const { imageSize } = useContext(TileContext) as TileContextProps;

    const imageWrapperRef = useRef(null);

    const {
        item,
        index,
        itemsPerRow,
        showLightBox,
        hideDivider,
        hideProductDetails,
        hideImagePadding,
        isHovering,
        relay,
        showSwipeIndicator,
    } = props;

    const itemServiceId = get(item, 'serviceId');

    const [swiperWasClicked, setSwiperWasClicked] = useState(false);
    const [wasHovered, setWasHovered] = useState(false);
    const [isSwipeIndicatorVisible, setIsSwipeIndicatorVisible] = useState(showSwipeIndicator);
    const [hasPageChanged, setHasPageChanged] = useState(false);

    useEffect(() => {
        if (isHovering && !wasHovered) {
            setWasHovered(true);
        }
    }, [isHovering, wasHovered]);

    useEffect(() => {
        setIsSwipeIndicatorVisible(showSwipeIndicator);
    }, [showSwipeIndicator]);

    const onSwiperPageChange = (nextPage: number): void => {
        tracking.trackEvent({
            category: 'results',
            action: 'results page image carousel arrow click',
            label: nextPage + 1,
            additional: { itemId: itemServiceId },
        });

        setHasPageChanged(true);

        if (!swiperWasClicked) {
            relay.refetch({ itemId: [itemServiceId], photosLimit: null });
            setSwiperWasClicked(true);
        }
    };

    const productClass = classnames(styles.product, {
        [styles.hasLightBox]: showLightBox,
        [styles.hasDivider]: !hideDivider,
    });

    const imageWrapperClass = classnames(styles.imageWrapper, styles.hideImageSidePadding, {
        [styles.hideImagePadding]: hideImagePadding,
    });

    const imageProps = {
        imageSize: imageSize,
        disableLinks: props.disableLinks,
        onClick: props.onClick,
        pdpUrl: props.pdpUrl,
        srcSetSizes: props.srcSetSizes,
        srcSetWidths: props.srcSetWidths,
        srcSetQuality: props.srcSetQuality,
        title: props.title,
        imageLoadVerticalOffset: props.imageLoadVerticalOffset,
        onContentLoaded: props.onContentLoaded,
        useLoFiLazyLoader: props.useLoFiLazyLoader,
    };

    const hasSingleImage = item?.carouselPhotos?.length === 1;

    return (
        <div className={productClass} draggable="false">
            <div className={imageWrapperClass} ref={imageWrapperRef}>
                <ProductSwiper
                    item={item}
                    index={index}
                    arrowsVisible={isHovering && !props.isQuickViewActive}
                    isInitiallyVisibleTile={index < itemsPerRow}
                    isMobile={props.isMobile}
                    itemsPerRow={props.itemsPerRow}
                    swiperWasClicked={swiperWasClicked}
                    wasHovered={wasHovered}
                    onPageChange={onSwiperPageChange}
                    imageProps={imageProps}
                    onSwipeStart={() => setIsSwipeIndicatorVisible(false)}
                    onSwipeEnd={() => setIsSwipeIndicatorVisible(true)}
                />
                {showSwipeIndicator && !hasSingleImage && (
                    <div
                        className={classnames(styles.swipeIndicator, {
                            [styles.isVisible]: !hasPageChanged && isSwipeIndicatorVisible,
                        })}
                    >
                        <FormattedMessage
                            defaultMessage="Swipe"
                            id="productTile.swipeIndicatorText"
                        />
                        <ArrowRight className={styles.swipeIndicatorArrow} />
                    </div>
                )}
            </div>
            {!hideProductDetails && (
                <ProductDetails
                    hideDivider={props.hideDivider}
                    showMoreOptionsBadge={props.showMoreOptionsBadge}
                    showQuickView={props.showQuickView}
                    openQuickView={props.openQuickView}
                    showQuickViewIcon={props.showQuickViewIcon}
                    dimensions={props.dimensions}
                    pdpUrl={props.pdpUrl}
                />
            )}
            {showSwipeIndicator && !hasSingleImage && (
                <VisibilityTracker
                    elementRef={imageWrapperRef}
                    onVisibilityChange={({ boundingClientRect, disconnect }) => {
                        if (boundingClientRect?.top < 0) {
                            setHasPageChanged(true);
                            disconnect();
                        }
                    }}
                    watchAfterFirstVisible
                />
            )}
        </div>
    );
};

ProductCarouselComponent.defaultProps = {
    disableLinks: false,
    itemsPerRow: 3,
    useLoFiLazyLoader: false,
    showMoreOptionsBadge: false,
};

export const ProductCarousel = createRefetchContainer(
    ProductCarouselComponent,
    {
        item: graphql`
            fragment ProductCarousel_item on Item
            @argumentDefinitions(photosLimit: { type: "Int", defaultValue: 2 }) {
                serviceId
                carouselPhotos: photos(limit: $photosLimit) {
                    __typename
                }
                ...ProductSwiper_item @arguments(photosLimit: $photosLimit)
            }
        `,
    },
    graphql`
        query ProductCarouselRefetchQuery($itemId: [String]!, $photosLimit: Int) {
            viewer {
                itemRead(itemIds: $itemId) {
                    ... on Item {
                        ...ProductSwiper_item @arguments(photosLimit: $photosLimit)
                    }
                }
            }
        }
    `
);
